/**
 * Fixed geographic tiles for POI seeding — United States (v1).
 * - Lower 48: regular grid over one bbox (no variable rows/cols in Admin).
 * - Alaska: two rectangles (avoids antimeridian issues with Overpass).
 * - Hawaii: one rectangle.
 *
 * Tile ids are stable: use these as `tileKey` in Firestore `poiSeedCoverage` docs
 * (`{tileId}__{category}`) so imports can resume after failures.
 */

const LOWER_48_BBOX = {
  south: 24.396308,
  west: -125.0,
  north: 49.384358,
  east: -66.93457,
};

/** @param {string} prefix e.g. 'usa_l48' */
function buildGridTiles(prefix, bbox, rows, cols) {
  const { south, west, north, east } = bbox;
  const tiles = [];
  const latStep = (north - south) / rows;
  const lngStep = (east - west) / cols;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      tiles.push({
        id: `${prefix}_r${r}_c${c}`,
        bbox: {
          south: south + r * latStep,
          north: south + (r + 1) * latStep,
          west: west + c * lngStep,
          east: west + (c + 1) * lngStep,
        },
      });
    }
  }
  return tiles;
}

/** Version id stored on coverage docs; bump when you change this grid. */
export const USA_POI_TILE_SET_ID = 'usa_v1';

/** 8×12 lower-48 grid + 2 Alaska segments + Hawaii = 99 tiles */
export const USA_POI_TILES = [
  ...buildGridTiles('usa_l48', LOWER_48_BBOX, 8, 12),
  {
    id: 'usa_ak_0',
    bbox: {
      south: 51.215399481,
      west: 172.476084832,
      north: 71.412502346,
      east: 180,
    },
  },
  {
    id: 'usa_ak_1',
    bbox: {
      south: 51.215399481,
      west: -180,
      north: 71.412502346,
      east: -129.9892472,
    },
  },
  {
    id: 'usa_hi',
    bbox: {
      south: 18.910361,
      west: -178.334698,
      north: 28.402123,
      east: -154.806773,
    },
  },
];

export const USA_POI_TILE_COUNT = USA_POI_TILES.length;

/** Fast lookup for coverage docs keyed by permanent tile id (e.g. usa_l48_r0_c0). */
export const USA_TILE_ID_SET = new Set(USA_POI_TILES.map((t) => t.id));

/**
 * True if a poiSeedCoverage doc belongs to the fixed USA v1 tile set.
 * Matches tileSetId === usa_v1 or legacy docs that already use a usa_v1 tileKey.
 */
export function isUsaV1CoverageDoc(doc) {
  if (!doc) return false;
  if (doc.tileSetId === USA_POI_TILE_SET_ID) return true;
  if (doc.tileKey && USA_TILE_ID_SET.has(String(doc.tileKey))) return true;
  return false;
}
